import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useEstablishment } from '../components/EstablishmentSelector';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
    Users, Clock, RefreshCw, Sun, Sofa, Wine, Package,
    Plus, Minus, Trash2, ChefHat, Send, CreditCard, Check,
    X, AlertCircle, User, Timer, Utensils, MessageSquare,
    Pause, Ban, Coffee, ArrowLeft, MapPin
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import TableDetailViewV2 from '../components/TableDetailViewV2';
import CompactTableCard from '../components/CompactTableCard';
import MobilePageHeader from '../components/MobilePageHeader';

// ======================== CONFIGURATION ========================

const ZONE_CONFIG = {
    terrasse: { label: 'Terrasse', icon: Sun, color: 'from-amber-500 to-orange-500' },
    salle: { label: 'Salle', icon: Sofa, color: 'from-blue-500 to-indigo-500' },
    bar: { label: 'Bar', icon: Wine, color: 'from-purple-500 to-pink-500' },
    emporter: { label: 'À Emporter', icon: Package, color: 'from-teal-500 to-green-500' }
};

// Simplified status colors: Green (libre), Orange (occupée), Yellow (à débarrasser)
const STATUS_CONFIG = {
    available: { label: 'Libre', bg: 'bg-emerald-500', bgLight: 'bg-emerald-50', border: 'border-emerald-400', text: 'text-emerald-700', icon: '🟢' },
    libre: { label: 'Libre', bg: 'bg-emerald-500', bgLight: 'bg-emerald-50', border: 'border-emerald-400', text: 'text-emerald-700', icon: '🟢' },
    occupied: { label: 'Occupée', bg: 'bg-orange-500', bgLight: 'bg-orange-50', border: 'border-orange-400', text: 'text-orange-700', icon: '🟠' },
    occupee: { label: 'Occupée', bg: 'bg-orange-500', bgLight: 'bg-orange-50', border: 'border-orange-400', text: 'text-orange-700', icon: '🟠' },
    sent_to_kitchen: { label: 'En cuisine', bg: 'bg-blue-500', bgLight: 'bg-blue-50', border: 'border-blue-400', text: 'text-blue-700', icon: '👨‍🍳' },
    en_preparation: { label: 'En préparation', bg: 'bg-blue-500', bgLight: 'bg-blue-50', border: 'border-blue-400', text: 'text-blue-700', icon: '👨‍🍳' },
    ready: { label: 'Prête', bg: 'bg-purple-500', bgLight: 'bg-purple-50', border: 'border-purple-400', text: 'text-purple-700', icon: '✅' },
    prete: { label: 'Prête', bg: 'bg-purple-500', bgLight: 'bg-purple-50', border: 'border-purple-400', text: 'text-purple-700', icon: '✅' },
    paid: { label: 'Payée', bg: 'bg-gray-400', bgLight: 'bg-gray-50', border: 'border-gray-300', text: 'text-gray-600', icon: '💰' },
    payee: { label: 'Payée', bg: 'bg-gray-400', bgLight: 'bg-gray-50', border: 'border-gray-300', text: 'text-gray-600', icon: '💰' },
    brouillon: { label: 'Brouillon', bg: 'bg-amber-500', bgLight: 'bg-amber-50', border: 'border-amber-400', text: 'text-amber-700', icon: '📝' },
    a_debarrasser: { label: 'À débarrasser', bg: 'bg-yellow-500', bgLight: 'bg-yellow-50', border: 'border-yellow-400', text: 'text-yellow-700', icon: '🧹' }
};

const ZONE_ORDER = ['terrasse', 'salle', 'bar', 'emporter'];

// ======================== HELPERS ========================

const formatCFA = (amount) => {
    if (!amount || amount === 0) return '0 F';
    return `${Math.round(amount).toLocaleString('fr-FR')} F`;
};

const calculateElapsedTime = (openedAt) => {
    if (!openedAt) return null;
    const opened = new Date(openedAt);
    const now = new Date();
    const diffMs = now - opened;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return '< 1 min';
    if (diffMins < 60) return `${diffMins} min`;
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hours}h${mins > 0 ? String(mins).padStart(2, '0') : ''}`;
};

// ======================== MAIN COMPONENT ========================

const TablesPage = () => {
    const { api, hasPermission, user } = useAuth();
    const { selectedEstablishment } = useEstablishment();
    const navigate = useNavigate();
    
    const [tables, setTables] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [selectedTable, setSelectedTable] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);
    
    // Permissions centralisées
    const isAdmin = user?.role === 'ADMIN';
    const isManager = ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT'].includes(user?.role);
    const isServer = ['SERVEUR', 'SERVEUSE', 'MAITRE_HOTEL'].includes(user?.role);
    const canViewAllTables = isManager;
    const canCancelOrder = isManager;
    
    // Fetch tables
    const fetchTables = useCallback(async (showRefresh = false) => {
        try {
            if (showRefresh) setRefreshing(true);
            const res = await api().get('/tables');
            setTables(res.data || []);
        } catch (error) {
            console.error('Error fetching tables:', error);
            toast.error('Erreur chargement tables');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [api]);
    
    useEffect(() => {
        fetchTables();
        const interval = setInterval(() => fetchTables(), 60000); // Rafraîchissement toutes les 60 secondes (1 minute)
        return () => clearInterval(interval);
    }, [fetchTables]);
    
    // Group tables by zone
    const tablesByZone = useMemo(() => {
        const grouped = {};
        ZONE_ORDER.forEach(zone => {
            grouped[zone] = { tables: [], total: 0, occupied: 0 };
        });
        
        tables.forEach(table => {
            const zone = (table.zone || '').toLowerCase();
            if (grouped[zone]) {
                grouped[zone].tables.push(table);
                grouped[zone].total++;
                if (['occupee', 'occupied', 'a_debarrasser', 'payee'].includes(table.status)) {
                    grouped[zone].occupied++;
                }
            }
        });
        
        Object.values(grouped).forEach(zone => {
            zone.tables.sort((a, b) => a.number - b.number);
        });
        
        return grouped;
    }, [tables]);
    
    // Stats
    const stats = useMemo(() => {
        const total = tables.length;
        const libre = tables.filter(t => ['libre', 'available', 'free'].includes(t.status)).length;
        const brouillon = tables.filter(t => t.status === 'brouillon').length;
        const occupied = tables.filter(t => ['occupee', 'occupied', 'sent_to_kitchen', 'en_preparation', 'prete', 'ready', 'en_attente_paiement'].includes(t.status)).length;
        const toClear = tables.filter(t => ['a_debarrasser', 'payee', 'paid'].includes(t.status)).length;
        return { total, libre, brouillon, occupied, toClear };
    }, [tables]);
    
    // Check for pending cart from POS
    const [pendingCart, setPendingCart] = useState(null);
    
    useEffect(() => {
        const savedCart = localStorage.getItem('pos_pending_cart');
        if (savedCart) {
            try {
                const cartData = JSON.parse(savedCart);
                if (cartData && cartData.length > 0) {
                    setPendingCart(cartData);
                }
            } catch (e) {
                console.error('Error parsing pending cart:', e);
            }
        }
    }, []);
    
    // Direct table click - opens detail view
    const handleTableClick = async (table) => {
        // Check permissions for servers
        if (user?.role === 'SERVEUR') {
            if (table.status !== 'libre' && table.waiter_id !== user.id) {
                toast.error('Cette table ne vous est pas assignée');
                return;
            }
        }
        
        // If there's a pending cart from POS, assign it to the table
        if (pendingCart && pendingCart.length > 0 && table.status === 'libre') {
            try {
                // Create order with pending cart items
                const orderData = {
                    order_type: 'table',
                    table_id: table.id,
                    table_number: String(table.number),
                    items: pendingCart.map(item => ({
                        product_id: item.product_id || item.id,
                        quantity: item.quantity || 1,
                        unit_price: item.unit_price || item.price,
                        product_name: item.name || item.product_name
                    }))
                };
                
                const res = await api().post('/pos/orders', orderData);
                toast.success(`${pendingCart.length} produit(s) ajouté(s) à la Table ${table.number}`);
                
                // Clear pending cart
                localStorage.removeItem('pos_pending_cart');
                setPendingCart(null);
                
                // Refresh tables and open detail
                await fetchTables();
                const updatedTable = { ...table, status: 'occupee', current_order_id: res.data?.order_id };
                setSelectedTable(updatedTable);
                setDetailOpen(true);
                return;
            } catch (error) {
                console.error('Error assigning cart to table:', error);
                const errMsg = error.response?.data?.detail;
                toast.error(typeof errMsg === 'string' ? errMsg : 'Erreur affectation');
            }
        }
        
        // Normal flow - just open detail view
        setSelectedTable(table);
        setDetailOpen(true);
    };
    
    const handleDetailClose = () => {
        setDetailOpen(false);
        setSelectedTable(null);
    };
    
    const handleTableUpdate = () => {
        fetchTables(true);
    };
    
    // Reset tables (admin only)
    const resetTables = async () => {
        if (!confirm('Réinitialiser toutes les tables ?')) return;
        try {
            const res = await api().post('/tables/reset');
            toast.success(res.data.message);
            fetchTables(true);
        } catch (error) {
            toast.error('Erreur réinitialisation');
        }
    };
    
    // Cancel all occupied tables (admin only)
    const cancelAllTables = async () => {
        if (!confirm('Annuler TOUTES les tables occupées ? Cette action est irréversible.')) return;
        
        try {
            const occupiedTables = tables.filter(t => t.status !== 'libre');
            let cancelled = 0;
            
            for (const table of occupiedTables) {
                try {
                    // Cancel order if exists
                    if (table.current_order_id) {
                        await api().post(`/pos/orders/${table.current_order_id}/cancel`, { reason: 'Annulation globale admin' });
                    }
                    // Clear table
                    await api().post(`/tables/${table.id}/clear`);
                    cancelled++;
                } catch (e) {
                    console.error(`Error cancelling table ${table.number}:`, e);
                }
            }
            
            toast.success(`${cancelled} table(s) annulée(s)`);
            fetchTables(true);
        } catch (error) {
            toast.error('Erreur annulation');
        }
    };
    
    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
            </div>
        );
    }
    
    return (
        <div className="p-2 sm:p-3 lg:p-4 space-y-2 sm:space-y-3" data-testid="tables-page">
            {/* Mobile Page Header */}
            <MobilePageHeader 
                title="Tables"
                subtitle={`${stats.libre} libres • ${stats.occupied} occupées`}
                icon={MapPin}
                backPath="/dashboard"
            />
            
            {/* Back to POS Button - Hidden on mobile (using MobilePageHeader) */}
            <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigate('/pos')}
                className="hidden md:flex h-8 text-xs"
            >
                <ArrowLeft className="w-3 h-3 mr-1" />
                Retour caisse
            </Button>
            
            {/* Pending Cart Banner - Compact */}
            {pendingCart && pendingCart.length > 0 && (
                <div className="bg-blue-500 text-white px-3 py-2 rounded-lg flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                        <Utensils className="w-4 h-4" />
                        <span className="font-medium">
                            {pendingCart.length} produit(s) → Sélectionnez une table
                        </span>
                    </div>
                    <Button 
                        variant="secondary" 
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => {
                            localStorage.removeItem('pos_pending_cart');
                            setPendingCart(null);
                            toast.info('Panier annulé');
                        }}
                    >
                        <X className="w-3 h-3 mr-1" />
                        Annuler
                    </Button>
                </div>
            )}
            
            {/* Restaurant Header - Minimal */}
            {selectedEstablishment && (
                <div 
                    className="px-3 py-1.5 rounded-lg text-white text-center text-sm font-semibold"
                    style={{ backgroundColor: selectedEstablishment.color || '#1e40af' }}
                >
                    {selectedEstablishment.name}
                </div>
            )}
            
            {/* Compact Stats Legend */}
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs sm:text-sm">
                <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 rounded bg-emerald-500" />
                    <span className="font-medium">{stats.libre} Libres</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 rounded bg-amber-400" />
                    <span className="font-medium">{stats.brouillon} Brouillon</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 rounded bg-orange-500" />
                    <span className="font-medium">{stats.occupied} Occupées</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 rounded bg-yellow-400" />
                    <span className="font-medium">{stats.toClear} À débarrasser</span>
                </div>
            </div>
            
            {/* Header Actions - Compact */}
            <div className="flex items-center justify-between">
                <h2 className="text-base font-bold">Plan des tables</h2>
                <div className="flex items-center gap-1.5">
                    {isAdmin && (stats.occupied > 0 || stats.toClear > 0) && (
                        <Button variant="outline" size="sm" onClick={cancelAllTables} className="h-7 text-xs text-orange-600 border-orange-300 hover:bg-orange-50">
                            <Ban className="h-3 w-3 mr-1" />
                            Annuler ({stats.occupied + stats.toClear})
                        </Button>
                    )}
                    {isAdmin && (
                        <Button variant="outline" size="sm" onClick={resetTables} className="h-7 text-xs text-red-600">
                            <AlertCircle className="h-3 w-3" />
                        </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => fetchTables(true)} disabled={refreshing} className="h-7 w-7 p-0">
                        <RefreshCw className={cn("h-4 w-4", refreshing && "animate-spin")} />
                    </Button>
                </div>
            </div>
            
            {/* Server Warning - Compact */}
            {isServer && !canViewAllTables && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-800 flex items-center gap-2">
                    <User className="h-3 w-3 flex-shrink-0" />
                    Vos tables assignées uniquement
                </div>
            )}
            
            {/* Tables by Zone - Compact Grid */}
            {ZONE_ORDER.map(zone => {
                const config = ZONE_CONFIG[zone];
                const data = tablesByZone[zone];
                if (!data || data.tables.length === 0) return null;
                
                const ZoneIcon = config.icon;
                
                // Filter for servers - only show their tables or free tables
                let displayTables = data.tables;
                if (isServer && !canViewAllTables) {
                    displayTables = data.tables.filter(t => 
                        // Tables libres visibles par tous (pour pouvoir les prendre)
                        t.status === 'libre' || t.status === 'available' || t.status === 'free' ||
                        // Tables assignées au serveur
                        t.waiter_id === user.id ||
                        t.assigned_to_id === user?.id ||
                        t.server_id === user?.id
                    );
                    if (displayTables.length === 0) return null;
                }
                
                return (
                    <div key={zone} className="space-y-2">
                        {/* Zone Header - More compact */}
                        <div className={cn(
                            "flex items-center justify-between px-3 py-1.5 rounded-lg text-white bg-gradient-to-r",
                            config.color
                        )}>
                            <div className="flex items-center gap-2">
                                <ZoneIcon className="h-4 w-4" />
                                <h3 className="font-semibold text-sm">{config.label}</h3>
                            </div>
                            <span className="text-xs opacity-90">
                                {data.occupied}/{data.total}
                            </span>
                        </div>
                        
                        {/* Compact Table Grid - More columns */}
                        <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 xl:grid-cols-12 gap-1.5 sm:gap-2">
                            {displayTables.map(table => (
                                <CompactTableCard
                                    key={table.id}
                                    table={table}
                                    onClick={handleTableClick}
                                    disabled={false}
                                />
                            ))}
                        </div>
                    </div>
                );
            })}
            
            {/* Table Detail View - Using new V2 component with draft/sent items */}
            <TableDetailViewV2
                table={selectedTable}
                open={detailOpen}
                onClose={handleDetailClose}
                onUpdate={handleTableUpdate}
                currentUser={user}
            />
        </div>
    );
};

export default TablesPage;
